// AdminSettingModal.js
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  TextField,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  DialogActions,
  Typography,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  OutlinedInput,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import "./AdminSettingModal.css";
import { config } from "../../config";
import axios from "../../APIs/axios";

const AdminSettingModal = ({ userDetails, onClose }) => {

  const [hseAllData, setHseAllData] = useState([]);
  const [adminSettings, setAdminSettings] = useState([]);
  // Mock data for score settings
  const [scoreSettings, setScoreSettings] = useState([]);

  // Dialog state for edit/add (admin settings)
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    sector_type: "",
    criticality_options: "",
    status_options: "",
  });


  useEffect(() => {
    getAllHseData();
    getAllSelectOptions();
    getAllScoreSettings();
  }, []);

  const getAllScoreSettings = async () => {
    try {
      const res = await axios.get(`${config.PATH}/api/admin-setting/hse-scoring`);
      setScoreSettings(res?.data?.data);
    } catch (err) {
      console.log(err.message);
      alert("Failed to load score settings. Please try again.");
    }
  }

  const getAllSelectOptions = async () => {
    try {
      const res = await axios.get(`${config.PATH}/api/admin-setting/select-options`);
      setAdminSettings(res?.data?.data);

    } catch (err) {
      console.log(err.message);
      alert("Failed to load select options. Please try again.");
    }
  }

  const getAllHseData = async () => {
    try {
      const data = await axios.get(`${config.PATH}/api/hse-allData`);

      setHseAllData(data);
    } catch (err) {
      console.log(err.message);
      alert("Failed to load HSE data. Please try again.");
    }
  };

  const sectors = () => {
    try {
      const options = [];
      const list = [
        ...new Set(
          (hseAllData?.data || [])
            .map((e) => e.sector_type)
            .filter(
              (sector_type) =>
                sector_type !== null &&
                sector_type !== undefined &&
                sector_type !== ""
            )
        ),
      ];
      list.map((area) => options.push({ label: area, value: area }));
      return options;
    } catch (err) {
      console.log(err);
    }
  };



  // Dialog state for edit/add (score settings)
  const [scoreDialogOpen, setScoreDialogOpen] = useState(false);
  const [scoreEditingId, setScoreEditingId] = useState(null);
  const [scoreFormData, setScoreFormData] = useState({
    sector_type: "",
    criticality: "",
    status: "",
    max_pts: "",
    earned: "",
  });

  // Delete confirmation state
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteType, setDeleteType] = useState(""); // "admin" or "score"

  const handleAddNew = () => {
    setEditingId(null);
    setFormData({
      sector_type: "",
      criticality_options: "",
      status_options: "",
    });
    setDialogOpen(true);
  };

  // const handleEdit = (id) => {
  //   const setting = adminSettings.find((s) => s.id === id);
  //   if (setting) {
  //     setEditingId(id);
  //     setFormData({
  //       sector_type: setting.sector_type,
  //       criticality_options: setting.criticality_options,
  //       status_options: setting.status_options,
  //     });
  //     setDialogOpen(true);
  //   }
  // };

  const handleEdit = (id) => {
    const setting = adminSettings.find((s) => s.id === id);

    if (setting) {
      setEditingId(id);

      setFormData({
        sector_type: setting.sector_type,

        criticality_options: Array.isArray(
          setting.criticality_options
        )
          ? setting.criticality_options.join(", ")
          : setting.criticality_options || "",

        status_options: Array.isArray(setting.status_options)
          ? setting.status_options.join(", ")
          : setting.status_options || "",
      });

      setDialogOpen(true);
    }
  };

  const handleDelete = (id) => {
    setDeletingId(id);
    setDeleteType("admin");
    setDeleteConfirmationOpen(true);
  };

  const confirmDelete = async () => {
    try {
      if (deleteType === "admin") {
        const setting = adminSettings.find((s) => s.id === deletingId);
        if (setting) {
          await axios.delete(`${config.PATH}/api/admin-setting/select-options/${setting.sector_type}`);
        }
        setAdminSettings(adminSettings.filter((s) => s.id !== deletingId));
      } else if (deleteType === "score") {
        const setting = scoreSettings.find((s) => s.id === deletingId);
        if (setting) {
          await axios.delete(`${config.PATH}/api/admin-setting/hse-scoring/${setting.id}`);
        }
        setScoreSettings(scoreSettings.filter((s) => s.id !== deletingId));
      }
      setDeleteConfirmationOpen(false);
      setDeletingId(null);
      setDeleteType("");
    } catch (err) {
      console.log(err.message);
      alert("Failed to delete. Please try again.");
    }
  };

  // Score settings handlers
  const handleAddScore = () => {
    setScoreEditingId(null);
    setScoreFormData({
      sector_type: "",
      criticality: "",
      status: "",
      max_pts: "",
      earned: "",
    });
    setScoreDialogOpen(true);
  };

  const handleEditScore = (id) => {
    const setting = scoreSettings.find((s) => s.id === id);
    if (setting) {
      setScoreEditingId(id);
      setScoreFormData({
        sector_type: setting.sector_type,
        criticality: setting.criticality,
        status: setting.status,
        max_pts: setting.max_pts,
        earned: setting.earned,
      });
      setScoreDialogOpen(true);
    }
  };

  const handleDeleteScore = (id) => {
    setDeletingId(id);
    setDeleteType("score");
    setDeleteConfirmationOpen(true);
  };

  const handleSaveScore = async () => {
    if (!scoreFormData.sector_type.trim() || !scoreFormData.criticality.trim() || !scoreFormData.status.trim()) {
      alert("Sector Type, Criticality, and Status are required");
      return;
    }

    try {
      if (scoreEditingId) {
        // Update existing
        await axios.put(`${config.PATH}/api/admin-setting/hse-scoring/${scoreEditingId}`, {
          sector_type: scoreFormData.sector_type,
          criticality: scoreFormData.criticality,
          status: scoreFormData.status,
          max_pts: scoreFormData.max_pts || "0",
          earned: scoreFormData.earned || "0",
        });
        setScoreSettings(
          scoreSettings.map((s) =>
            s.id === scoreEditingId
              ? {
                ...s,
                sector_type: scoreFormData.sector_type,
                criticality: scoreFormData.criticality,
                status: scoreFormData.status,
                max_pts: scoreFormData.max_pts || "0",
                earned: scoreFormData.earned || "0",
              }
              : s
          )
        );
      } else {
        // Add new
        const newId = Math.max(...scoreSettings.map((s) => s.id), 0) + 1;
        await axios.post(`${config.PATH}/api/admin-setting/hse-scoring`, {
          sector_type: scoreFormData.sector_type,
          criticality: scoreFormData.criticality,
          status: scoreFormData.status,
          max_pts: scoreFormData.max_pts || "0",
          earned: scoreFormData.earned || "0",
        });
        setScoreSettings([
          ...scoreSettings,
          {
            id: newId,
            sector_type: scoreFormData.sector_type,
            criticality: scoreFormData.criticality,
            status: scoreFormData.status,
            max_pts: scoreFormData.max_pts || "0",
            earned: scoreFormData.earned || "0",
          },
        ]);
      }
      await getAllScoreSettings();
      setScoreDialogOpen(false);
    } catch (err) {
      console.log(err.message);
      alert("Failed to save score. Please try again.");
    }
  };

  const getSectorOptions = (sectorType) => {
    const selectedSector = adminSettings.find(
      (item) => item.sector_type === sectorType
    );

    // const normalizeOptions = (options) => {
    //   // already array
    //   if (Array.isArray(options)) {
    //     return options
    //       .map((item) => String(item).trim())
    //       .filter(Boolean);
    //   }

    //   // string
    //   if (typeof options === "string") {
    //     return options
    //       .split(",")
    //       .map((item) => item.trim())
    //       .filter(Boolean);
    //   }

    //   return [];
    // };

    const normalizeOptions = (options) => {
      // If already array
      if (Array.isArray(options)) {
        return options
          .flatMap((item) =>
            String(item)
              .split(",")
              .map((val) => val.trim())
          )
          .filter(Boolean);
      }

      // If string
      if (typeof options === "string") {
        return options
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean);
      }

      return [];
    };

    return {
      criticalityOptions: normalizeOptions(
        selectedSector?.criticality_options
      ),
      statusOptions: normalizeOptions(
        selectedSector?.status_options
      ),
    };
  };

  const selectedSectorOptions = getSectorOptions(
    scoreFormData.sector_type
  );

  const handleScoreDialogClose = () => {
    setScoreDialogOpen(false);
    setScoreEditingId(null);
    setScoreFormData({
      sector_type: "",
      criticality: "",
      status: "",
      max_pts: "",
      earned: "",
    });
  };

  // const handleSave = async () => {
  //   if (!formData.sector_type.trim()) {
  //     alert("Sector Type is required");
  //     return;
  //   }

  //   try {
  //     if (editingId) {
  //       const setting = adminSettings.find((s) => s.id === editingId);
  //       if (setting) {
  //         await axios.put(`${config.PATH}/api/admin-setting/select-options/${setting.sector_type}`, {
  //           sector_type: formData.sector_type,
  //           criticality_options: [formData.criticality_options],
  //           status_options: [formData.status_options]
  //         });
  //       }
  //       // Update existing
  //       setAdminSettings(
  //         adminSettings.map((s) =>
  //           s.id === editingId
  //             ? {
  //               ...s,
  //               sector_type: formData.sector_type,
  //               criticality_options: formData.criticality_options,
  //               status_options: formData.status_options,
  //             }
  //             : s
  //         )
  //       );
  //     } else {
  //       // Add new
  //       const newId = Math.max(...adminSettings.map((s) => s.id), 0) + 1;
  //       await axios.post(`${config.PATH}/api/admin-setting/select-options`, {
  //         sector_type: formData.sector_type,
  //         criticality_options: [formData.criticality_options],
  //         status_options: [formData.status_options]
  //       });
  //       setAdminSettings([
  //         ...adminSettings,
  //         {
  //           id: newId,
  //           sector_type: formData.sector_type,
  //           criticality_options: formData.criticality_options,
  //           status_options: formData.status_options,
  //         },
  //       ]);
  //     }
  //     await getAllSelectOptions();
  //     setDialogOpen(false);
  //   } catch (err) {
  //     console.log(err.message);
  //     alert("Failed to save. Please try again.");
  //   }
  // };

  const handleSave = async () => {
    if (!formData.sector_type.trim()) {
      alert("Sector Type is required");
      return;
    }

    // Convert comma-separated text into array
    const criticalityOptionsArray = formData.criticality_options
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);

    const statusOptionsArray = formData.status_options
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);

    try {
      if (editingId) {
        const setting = adminSettings.find((s) => s.id === editingId);

        if (setting) {
          await axios.put(
            `${config.PATH}/api/admin-setting/select-options/${setting.sector_type}`,
            {
              sector_type: formData.sector_type,
              criticality_options: criticalityOptionsArray,
              status_options: statusOptionsArray,
            }
          );
        }

        // Update local state
        setAdminSettings(
          adminSettings.map((s) =>
            s.id === editingId
              ? {
                ...s,
                sector_type: formData.sector_type,
                criticality_options: criticalityOptionsArray,
                status_options: statusOptionsArray,
              }
              : s
          )
        );
      } else {
        // Add new
        const newId =
          Math.max(...adminSettings.map((s) => s.id), 0) + 1;

        await axios.post(
          `${config.PATH}/api/admin-setting/select-options`,
          {
            sector_type: formData.sector_type,
            criticality_options: criticalityOptionsArray,
            status_options: statusOptionsArray,
          }
        );

        setAdminSettings([
          ...adminSettings,
          {
            id: newId,
            sector_type: formData.sector_type,
            criticality_options: criticalityOptionsArray,
            status_options: statusOptionsArray,
          },
        ]);
      }

      await getAllSelectOptions();

      setDialogOpen(false);
    } catch (err) {
      console.log(err.message);
      alert("Failed to save. Please try again.");
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingId(null);
    setFormData({
      sector_type: "",
      criticality_options: "",
      status_options: "",
    });
  };

  return (
    <>
      <Dialog open={true} onClose={onClose} fullWidth maxWidth="lg">
        <DialogTitle style={{ background: "#307268", color: "#efc71d" }}>
          Admin Settings
          <IconButton
            aria-label="close"
            style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              backgroundColor: "#efc71d",
            }}
            onClick={onClose}
            size="small"
          >
            <CloseIcon style={{ color: "#307248" }} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <div className="admin-settings-container">
            <Typography variant="h6" style={{ marginBottom: "15px", color: "#307268" }}>
              Select Option
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddNew}
              className="add-button"
              style={{ background: "#efc71d", color: "#307268" }}
            >
              Add New Sector
            </Button>

            <Table className="admin-settings-table">
              <TableHead>
                <TableRow className="table-header">
                  <TableCell>Sector Type</TableCell>
                  <TableCell>Criticality Options</TableCell>
                  <TableCell>Status Options</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {adminSettings.map((setting) => (
                  <TableRow key={setting.id} className="table-row">
                    <TableCell>{setting.sector_type}</TableCell>
                    <TableCell>
                      {Array.isArray(setting.criticality_options)
                        ? setting.criticality_options.join(", ")
                        : setting.criticality_options}
                    </TableCell>

                    <TableCell>
                      {Array.isArray(setting.status_options)
                        ? setting.status_options.join(", ")
                        : setting.status_options}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleEdit(setting.id)}
                        className="edit-button"
                        size="small"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDelete(setting.id)}
                        className="delete-button"
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Divider style={{ margin: "30px 0" }} />

            <Typography variant="h6" style={{ marginBottom: "15px", color: "#307268" }}>
              Select Score
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddScore}
              className="add-button"
              style={{ background: "#efc71d", color: "#307268" }}
            >
              Add New Score
            </Button>

            <Table className="admin-settings-table">
              <TableHead>
                <TableRow className="table-header">
                  <TableCell>Sector Type</TableCell>
                  <TableCell>Criticality</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Max Pts</TableCell>
                  <TableCell>Earned</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {scoreSettings.map((setting) => (
                  <TableRow key={setting.id} className="table-row">
                    <TableCell>{setting.sector_type}</TableCell>
                    <TableCell>{setting.criticality}</TableCell>
                    <TableCell>{setting.status}</TableCell>
                    <TableCell>{setting.max_pts}</TableCell>
                    <TableCell>{setting.earned}</TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleEditScore(setting.id)}
                        className="edit-button"
                        size="small"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteScore(setting.id)}
                        className="delete-button"
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit/Add Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle style={{ background: "#307268", color: "#efc71d" }}>
          {editingId ? "Edit Sector" : "Add New Sector"}
          <IconButton
            aria-label="close"
            style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              backgroundColor: "#efc71d",
            }}
            onClick={handleDialogClose}
            size="small"
          >
            <CloseIcon style={{ color: "#307248" }} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <div className="dialog-form">
            {/* <Select
              label="Sector Type"
              variant="outlined"
              value={formData.sector_type}
              onChange={(e) =>
                setFormData({ ...formData, sector_type: e.target.value })
              }
              fullWidth
              margin="normal"
              size="small"
              displayEmpty
              className="sector-type-select"
            >
              <MenuItem value="">
                <em>Select Sector Type</em>
              </MenuItem>
              {sectors()?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select> */}
            <FormControl
              variant="outlined"
              fullWidth
              margin="normal"
              size="small"
            >
              <InputLabel>Sector Type</InputLabel>

              <Select
                value={formData.sector_type}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    sector_type: e.target.value,
                  })
                }
                input={<OutlinedInput label="Sector Type" />}
              >
                <MenuItem value="">
                  <em>Select Sector Type</em>
                </MenuItem>

                {sectors()?.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Criticality Options (comma-separated)"
              variant="outlined"
              value={formData.criticality_options}
              onChange={(e) => {
                let value = e.target.value;

                // Auto capitalize each comma-separated word
                value = value.replace(/\b\w/g, (char) =>
                  char.toUpperCase()
                );

                setFormData({
                  ...formData,
                  criticality_options: value,
                });
              }}
              fullWidth
              margin="normal"
              size="small"
              placeholder="e.g., High, Medium, Low"
            />
            <TextField
              label="Status Options (comma-separated)"
              variant="outlined"
              value={formData.status_options}
              onChange={(e) =>
                setFormData({ ...formData, status_options: e.target.value.toUpperCase() })
              }
              fullWidth
              margin="normal"
              size="small"
              placeholder="e.g., YES, NO, PARTIAL, NA"
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDialogClose}
            style={{ color: "#307268" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            style={{ background: "#efc71d", color: "#307268" }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmationOpen}
        onClose={() => setDeleteConfirmationOpen(false)}
        maxWidth="xs"
      >
        <DialogTitle style={{ background: "#307268", color: "#efc71d" }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          Are you sure you want to delete this {deleteType === "score" ? "score" : "sector"} setting?
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmationOpen(false)}
            style={{ color: "#307268" }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            style={{ background: "#ff4d4d", color: "white" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Score Edit/Add Dialog */}
      <Dialog open={scoreDialogOpen} onClose={handleScoreDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle style={{ background: "#307268", color: "#efc71d" }}>
          {scoreEditingId ? "Edit Score" : "Add New Score"}
          <IconButton
            aria-label="close"
            style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              backgroundColor: "#efc71d",
            }}
            onClick={handleScoreDialogClose}
            size="small"
          >
            <CloseIcon style={{ color: "#307248" }} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <div className="dialog-form">
            <FormControl
              variant="outlined"
              fullWidth
              margin="normal"
              size="small"
            >
              <InputLabel>Sector Type</InputLabel>

              <Select
                value={scoreFormData.sector_type}
                onChange={(e) => {
                  const selectedSector = e.target.value;

                  setScoreFormData({
                    ...scoreFormData,
                    sector_type: selectedSector,
                    criticality: "",
                    status: "",
                  });
                }}
                input={<OutlinedInput label="Sector Type" />}
              >
                <MenuItem value="">
                  <em>Select Sector Type</em>
                </MenuItem>

                {sectors()?.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedSectorOptions.criticalityOptions.length > 0 ? (
              <FormControl
                variant="outlined"
                fullWidth
                margin="normal"
                size="small"
              >
                <InputLabel>Criticality</InputLabel>

                <Select
                  value={scoreFormData.criticality}
                  onChange={(e) =>
                    setScoreFormData({
                      ...scoreFormData,
                      criticality: e.target.value,
                    })
                  }
                  input={<OutlinedInput label="Criticality" />}
                >
                  <MenuItem value="">
                    <em>Select Criticality</em>
                  </MenuItem>

                  {selectedSectorOptions.criticalityOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                label="Criticality"
                variant="outlined"
                value={scoreFormData.criticality}
                onChange={(e) =>
                  setScoreFormData({
                    ...scoreFormData,
                    criticality: e.target.value,
                  })
                }
                fullWidth
                margin="normal"
                size="small"
              />
            )}
            {selectedSectorOptions.statusOptions.length > 0 ? (
              <FormControl
                variant="outlined"
                fullWidth
                margin="normal"
                size="small"
              >
                <InputLabel>Status</InputLabel>

                <Select
                  value={scoreFormData.status}
                  onChange={(e) =>
                    setScoreFormData({
                      ...scoreFormData,
                      status: e.target.value,
                    })
                  }
                  input={<OutlinedInput label="Status" />}
                >
                  <MenuItem value="">
                    <em>Select Status</em>
                  </MenuItem>

                  {selectedSectorOptions.statusOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                label="Status"
                variant="outlined"
                value={scoreFormData.status}
                onChange={(e) =>
                  setScoreFormData({
                    ...scoreFormData,
                    status: e.target.value,
                  })
                }
                fullWidth
                margin="normal"
                size="small"
              />
            )}
            <TextField
              label="Max Points"
              variant="outlined"
              type="number"
              value={scoreFormData.max_pts}
              onChange={(e) =>
                setScoreFormData({ ...scoreFormData, max_pts: e.target.value })
              }
              fullWidth
              margin="normal"
              size="small"
            />
            <TextField
              label="Earned Points"
              variant="outlined"
              type="number"
              value={scoreFormData.earned}
              onChange={(e) =>
                setScoreFormData({ ...scoreFormData, earned: e.target.value })
              }
              fullWidth
              margin="normal"
              size="small"
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleScoreDialogClose}
            style={{ color: "#307268" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveScore}
            variant="contained"
            style={{ background: "#efc71d", color: "#307268" }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdminSettingModal;
